import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import {
    Box,
    Button,
    Typography,
    Paper,
    Tooltip,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { useState, useEffect, useMemo } from 'react';

const SOATable = ({ items, onEdit, scrollThreshold = 768 }) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [currentPage, setCurrentPage] = useState(1);
    const sizePerPage = 10;

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const totalSDIEMO = useMemo(() =>
        items.reduce((sum, row) => {
            const cell = row.account_details || {};
            return sum + ((cell.security || 0) + (cell.interest || 0) + (cell.early_move_out_prorate || 0));
        }, 0),
        [items]);

    const totalFinal = useMemo(() =>
        items.reduce((sum, row) => {
            const cell = row.account_details || {};
            const security = cell.security || 0;
            const interest = cell.interest || 0;
            const prorate = cell.early_move_out_prorate || 0;
            const deductions = cell.deductions || [];
            const totalDeductions = deductions.reduce((s, d) => s + (d.cost_per_action || 0) * (d.number_of_actions || 0), 0);
            return sum + (security + interest + prorate - totalDeductions);
        }, 0),
        [items]);

    const getLastApprovalDate = (row) => {
        const auditorApproval = row.auditor_approval || {};
        if (auditorApproval.updated_at) {
            return new Date(auditorApproval.updated_at).toLocaleDateString();
        }

        const approvals = row.approvals || [];
        if (approvals.length > 0) {
            const latestApproval = approvals.reduce((latest, approval) => {
                if (!approval.updated_at) return latest;
                return !latest || new Date(approval.updated_at) > new Date(latest.updated_at) ? approval : latest;
            }, null);
            return latestApproval?.updated_at ? new Date(latestApproval.updated_at).toLocaleDateString() : 'N/A';
        }

        return 'N/A';
    };

    const getApprovalStatus = (row) => {
        const approvals = row.approvals || [];
        const auditorApproval = row.auditor_approval || {};
        const hasRejection =
            approvals.some((approval) => approval.status === 'rejected') ||
            auditorApproval.status === 'rejected';
        const allApprovalsDone =
            approvals.length > 0 && approvals.every((approval) => approval.status === 'approved');

        if (hasRejection) {
            return { text: 'Rejected', color: 'error.main' };
        } else if (allApprovalsDone && auditorApproval.status === 'approved') {
            return { text: 'Approved', color: 'success.main' };
        } else if (allApprovalsDone) {
            return { text: 'Awaiting Auditor', color: 'info.main' };
        } else {
            return { text: 'Pending', color: 'warning.main' };
        }
    };

    const handleEditClick = (row, e) => {
        e.stopPropagation();
        onEdit(row);
    };

    const columns = [
        {
            dataField: 'serialNo',
            text: 'S.No',
            sort: true,
            footer: '',
            footerFormatter: () => (
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 12 }}>
                    Total
                </Typography>
            ),
            formatter: (cell, row, rowIndex) => ((currentPage - 1) * sizePerPage) + (rowIndex + 1),
        },
        {
            dataField: 'tenant.0.name',
            text: 'Tenant Name',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell || 'N/A'} arrow>
                    <span>{cell || 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'property_details.unit_no',
            text: 'Unit No',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell || 'N/A'} arrow>
                    <span>{cell || 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'createdAt',
            text: 'Date Created On',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => {
                const date = cell ? new Date(cell).toLocaleDateString() : 'N/A';
                return (
                    <Tooltip title={date} arrow>
                        <span>{date}</span>
                    </Tooltip>
                );
            },
        },
        {
            dataField: 'account_details',
            text: 'Total Amt (SD+I+EMO)',
            sort: true,
            footer: 'Total SD+I+EMO',
            footerFormatter: () => `$${Number(totalSDIEMO).toLocaleString()}`,
            formatter: (cell) => {
                const security = cell?.security || 0;
                const interest = cell?.interest || 0;
                const prorate = cell?.early_move_out_prorate || 0;
                const total = security + interest + prorate;
                const securityFormatted = `$${Number(security).toLocaleString()}`;
                const interestFormatted = `$${Number(interest).toLocaleString()}`;
                const prorateFormatted = `$${Number(prorate).toLocaleString()}`;
                const totalFormatted = `$${Number(total).toLocaleString()}`;
                return (
                    <Tooltip title={`${securityFormatted} + ${interestFormatted} + ${prorateFormatted} = ${totalFormatted}`} arrow>
                        <span>{totalFormatted}</span>
                    </Tooltip>
                );
            },
        },
        {
            dataField: 'account_details',
            text: 'Final Amount',
            sort: true,
            footer: 'Total Final Amount',
            footerFormatter: () => {
                const displayValue = Math.abs(totalFinal).toLocaleString();
                const suffix = totalFinal < 0 ? ' (Deducted)' : ' (Refunded)';
                const color = totalFinal < 0 ? 'error.main' : 'success.main';
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            color,
                            fontWeight: 'medium',
                            fontSize: 12,
                        }}
                    >
                        ${displayValue}{suffix}
                    </Typography>
                );
            },
            formatter: (cell) => {
                const security = cell?.security || 0;
                const interest = cell?.interest || 0;
                const prorate = cell?.early_move_out_prorate || 0;
                const totalDeductions = cell?.deductions
                    ? cell.deductions.reduce(
                        (sum, deduction) => sum + deduction.cost_per_action * deduction.number_of_actions,
                        0
                    )
                    : 0;
                const finalAmount = security + interest + prorate - totalDeductions;
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            color: finalAmount < 0 ? 'error.main' : 'success.main',
                            fontWeight: 'medium',
                            fontSize: 12,
                        }}
                    >
                        ${Math.abs(finalAmount).toLocaleString()}
                        {finalAmount < 0 ? ' (Deducted)' : ' (Refunded)'}
                    </Typography>
                );
            },
        },
        {
            dataField: 'email_details.emailed_at',
            text: 'Mailed On',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell ? new Date(cell).toLocaleDateString() : 'N/A'} arrow>
                    <span>{cell ? new Date(cell).toLocaleDateString() : 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'landlord_details.name',
            text: 'Landlord',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell || 'N/A'} arrow>
                    <span>{cell || 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'last_approval_date',
            text: 'Last Approval',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell, row) => (
                <Tooltip title={getLastApprovalDate(row)} arrow>
                    <span>{getLastApprovalDate(row)}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'approvals',
            text: 'Approval Status',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell, row) => {
                const status = getApprovalStatus(row);
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            color: status.color,
                            fontWeight: 'medium',
                            fontSize: 12,
                            bgcolor: status.color + '.light',
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            display: 'inline-block',
                        }}
                    >
                        {status.text}
                    </Typography>
                );
            },
        },
        {
            dataField: 'actions',
            text: 'Actions',
            footer: '',
            footerFormatter: () => '',
            formatter: (cell, row) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Tooltip title="Edit" arrow>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => handleEditClick(row, e)}
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'white',
                                minWidth: '40px',
                                p: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': { bgcolor: 'primary.dark' },
                            }}
                        >
                            <FeatherIcon icon="edit" size={16} />
                        </Button>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const paginationOptions = {
        sizePerPage,
        hideSizePerPage: true,
        hidePageListOnlyOnePage: true,
        onPageChange: (page) => setCurrentPage(page),
    };

    const NoDataIndication = () => (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" width="100%">
            <Box textAlign="center" py={4}>
                <FeatherIcon icon="file-text" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <Typography variant="h6" color="text.secondary">
                    No Statements of Account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No SOAs available for preview
                </Typography>
            </Box>
        </Box>
    );

    const shouldShowVerticalScrollbar = windowWidth <= scrollThreshold;

    const tableStyles = {
        minHeight: '50vh',
        maxHeight: '75vh',
        overflowY: shouldShowVerticalScrollbar ? 'auto' : 'visible',
        overflowX: 'auto',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        bgcolor: 'background.paper',
        width: 'calc(100% - 30px)',
        minWidth: '600px',
        position: 'relative',
        '& .react-bootstrap-table': {
            minHeight: '50vh',
        },
        '& table': {
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            width: '100%',
            bgcolor: 'background.paper',
        },
        '& thead th': {
            position: 'sticky',
            top: 0,
            backgroundColor: 'grey.100',
            zIndex: 2,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            padding: '14px 10px',
            fontWeight: '600',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        },
        '& tbody td': {
            padding: '14px 10px',
            borderBottom: '1px solid',
            borderColor: 'grey.100',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.secondary',
            fontSize: 12,
            verticalAlign: 'middle',
        },
        '& tbody tr': {
            transition: 'background-color 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: 'grey.50',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            },
        },
        '& .pagination': {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '12px 16px',
            borderTop: '1px solid',
            borderColor: 'grey.200',
            bgcolor: 'grey.100',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            '& .page-link': {
                borderRadius: '4px',
                margin: '0 4px',
                color: 'primary.main',
                '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                },
            },
            '& .page-item.active .page-link': {
                backgroundColor: 'primary.main',
                color: 'white',
                borderColor: 'primary.main',
            },
        },
        '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'grey.100',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'grey.400',
            borderRadius: '4px',
            '&:hover': {
                background: 'grey.600',
            },
        },
        'scrollbar-width': 'thin',
        'scrollbar-color': 'grey.400 grey.100',
    };

    return (
        <Paper sx={tableStyles} className="soa-table-container miui-table scrollable-table">
            <BootstrapTable
                keyField="_id"
                data={items || []}
                columns={columns}
                pagination={paginationFactory(paginationOptions)}
                bootstrap4
                bordered={false}
                hover
                striped
                noDataIndication={NoDataIndication}
                footer={true}
                classes="px-2"
                rowEvents={{
                    onClick: (e, row) => {
                        onEdit(row);
                    },
                }}
                rowStyle={{ cursor: 'pointer' }}
            />
        </Paper>
    );
};

export { SOATable };